import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Palette, Clapperboard, Megaphone, Globe, Mic2, Radio, ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeIn, Reveal, SectionHeading, useDocTitle } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { GoldLink, GhostLink } from "../components/Buttons";
import { MediaImg } from "../components/MediaImg";
import { SpinWheel } from "../components/SpinWheel";
import { CTABanner } from "../components/CTABanner";
import { IMAGES, PROJECTS, TESTIMONIALS, TRUSTED_BY, TRUSTED_LOGOS, PROCESS_STEPS, VIDEOS } from "../data/site";

const SERVICES = [
  {
    icon: Clapperboard,
    num: "01",
    title: "Video Production",
    serif: "Our strongest lens.",
    copy: "Commercials, brand films, interviews, product video, drone, photography and post — premium production planned around what your business needs to achieve.",
    to: "/for-businesses",
    testId: "service-video",
  },
  {
    icon: Megaphone,
    num: "02",
    title: "Marketing & Content",
    serif: "Strategy, then momentum.",
    copy: "Content strategy, social, campaigns, advertising creative and monthly creation — an ongoing creative partner, not a one-off vendor.",
    to: "/marketing",
    testId: "service-marketing",
  },
  {
    icon: Palette,
    num: "03",
    title: "Branding & Design",
    serif: "Need a brand? We can build it.",
    copy: "Logos, identity systems, messaging and guidelines — complete brands from scratch, or rebrands for businesses that have outgrown their look.",
    to: "/branding",
    testId: "service-branding",
  },
  {
    icon: Globe,
    num: "04",
    title: "Web & Digital",
    serif: "Your hardest-working employee.",
    copy: "Website strategy and design, landing pages, and digital content built to convert visitors into customers.",
    to: "/marketing",
    testId: "service-web",
  },
  {
    icon: Mic2,
    num: "05",
    title: "Studio Builds",
    serif: "Rooms that create.",
    copy: "Podcast studios, video studios and content rooms — designed, installed, configured, and your team trained.",
    to: "/systems",
    testId: "service-studios",
  },
  {
    icon: Radio,
    num: "06",
    title: "Production & Events",
    serif: "When it's live, it's us.",
    copy: "Corporate production, livestreams, event AV, sound and lighting — the technical arm of the same creative team.",
    to: "/live-production",
    testId: "service-production",
  },
];

const FRAMEWORK = [
  {
    num: "01",
    word: "Story",
    tagline: "Figure out what matters.",
    copy: "Before anything gets designed or filmed, we get clear on what you're trying to say, who needs to hear it, and why they'll care.",
  },
  {
    num: "02",
    word: "Scene",
    tagline: "Make it worth watching.",
    copy: "Then we turn the idea into something people actually want to watch, see and experience — the commercial, the content, the campaign, the room.",
  },
  {
    num: "03",
    word: "Sound",
    tagline: "Make sure it's heard.",
    copy: "Finally we give it a voice and put it in front of people — through marketing, distribution, and the technical craft that makes it land.",
  },
];

const MARQUEE_ITEMS = [
  "Commercial Video",
  "Marketing Strategy",
  "Social Content",
  "Brand Films",
  "Photography",
  "Web Design",
  "Studio Builds",
  "Live Production",
];

const MARKETING_QUESTIONS = [
  "What should we talk about?",
  "Who are we trying to reach?",
  "What content should we make?",
  "What campaign could actually get attention?",
  "What should we film?",
  "Where should the content go?",
];

const WHEEL = [0, 7, 4, 8, 23, 5].map((i) => PROJECTS[i]);

export default function Home() {
  useDocTitle(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-36 sm:px-8 sm:pt-44 lg:grid-cols-12 lg:px-16">
          <div className="lg:col-span-7">
            <FadeIn delay={0.05}>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold-dark">
                Studio Solutions · Wisconsin · Story. Scene. Sound.
              </p>
            </FadeIn>
            <h1 className="headline mt-6 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-7xl">
              <MaskedLine delay={0.15}>Make something</MaskedLine>
              <MaskedLine delay={0.3}>
                <span className="text-gold-dark">people remember.</span>
              </MaskedLine>
            </h1>
            <FadeIn delay={0.7}>
              <p className="mt-7 max-w-xl font-serif text-lg italic leading-relaxed text-smoke sm:text-2xl">
                Studio Solutions helps businesses tell better stories, create better content — and
                get both in front of the people who matter.
              </p>
            </FadeIn>
            <FadeIn delay={0.85}>
              <div className="mt-9 flex flex-wrap gap-4">
                <GoldLink to="/start-a-project" testId="hero-cta-start-project">
                  Start a Project
                </GoldLink>
                <GhostLink to="/work" testId="hero-cta-see-work">
                  See Our Work
                </GhostLink>
              </div>
            </FadeIn>
            <FadeIn delay={1}>
              <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink/45">
                <li>Video</li>
                <li className="text-gold">◆</li>
                <li>Marketing</li>
                <li className="text-gold">◆</li>
                <li>Content</li>
                <li className="text-gold">◆</li>
                <li>Studio Builds</li>
                <li className="text-gold">◆</li>
                <li>Production</li>
              </ul>
            </FadeIn>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div style={{ y: imgY }} className="relative">
              <FadeIn delay={0.5}>
                <div className="clip-frame relative overflow-hidden shadow-2xl">
                  <video
                    src="/assets/media/fondy-showcase-loop.mp4"
                    poster="/assets/media/poster-fondy.jpg"
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="h-[420px] w-full object-cover sm:h-[540px]"
                    data-testid="hero-showcase-video"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-night/40 via-transparent to-transparent" />
                  <p className="absolute bottom-4 left-4 bg-night/50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-sand backdrop-blur-sm">
                    Fondy Famous × Laughable Productions
                  </p>
                </div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.05, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute -bottom-8 -left-4 flex items-center gap-4 border border-ink/10 bg-sand p-4 shadow-xl sm:-left-10"
                >
                  <MediaImg src={IMAGES.edit} alt="Edit suite" className="h-16 w-16 object-cover" />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">
                      In the Edit
                    </p>
                    <p className="font-display text-sm font-bold text-ink">Madison, WI</p>
                  </div>
                </motion.div>
              </FadeIn>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* CREATIVE PRODUCTION PARTNER */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-32 lg:px-16">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold-dark">
                Your Creative Production Partner
              </p>
            </Reveal>
            <h2 className="headline mt-5 font-display text-3xl font-bold leading-[1.15] tracking-tight text-ink sm:text-5xl">
              <Reveal delay={0.05}>Some projects start with a camera.</Reveal>
              <Reveal delay={0.12}>Some start with a question.</Reveal>
              <Reveal delay={0.19}>Some start with an empty room.</Reveal>
              <Reveal delay={0.26}>
                <span className="text-gold-dark">And some start with nothing more than an idea.</span>
              </Reveal>
            </h2>
            <Reveal delay={0.34}>
              <p className="mt-7 max-w-xl leading-relaxed text-smoke">
                Studio Solutions brings marketing, video, design and technical production together
                under one roof to turn ideas into finished experiences people actually notice.
              </p>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-ink/45">
                From concept to creation — <span className="text-gold-dark">Story. Scene. Sound.</span>
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-4">
                <video
                  src="/assets/media/kindly-loop.mp4"
                  poster="/assets/media/p-kindly.jpg"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="h-56 w-full object-cover sm:h-72"
                  data-testid="partner-story-video"
                />
                <video
                  src="/assets/media/autism-loop.mp4"
                  poster="/assets/media/p-autism.jpg"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="mt-10 h-56 w-full object-cover sm:h-72"
                  data-testid="partner-doc-video"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STORY. SCENE. SOUND. PHILOSOPHY */}
      <section className="bg-night text-sand">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-32 lg:px-16">
          <SectionHeading
            dark
            eyebrow="The Philosophy"
            title={<>Story. <span className="text-gold">Scene.</span> Sound.</>}
            sub="Three words born in S3 Films, our cinematic film division — now behind everything we make, from a 15-second reel to a full campaign."
          />
          <div className="mt-14 grid gap-px bg-sand/10 lg:grid-cols-3">
            {FRAMEWORK.map((f, i) => (
              <Reveal key={f.word} delay={i * 0.08} className="bg-night">
                <div
                  data-testid={`framework-${f.word.toLowerCase()}`}
                  className="h-full p-8 transition-colors duration-500 hover:bg-sand/5 sm:p-10"
                >
                  <p className="font-mono text-sm tracking-[0.3em] text-gold">{f.num}</p>
                  <h3 className="mt-5 font-display text-3xl font-extrabold tracking-tight">{f.word}.</h3>
                  <p className="mt-2 font-serif text-lg italic text-gold">{f.tagline}</p>
                  <p className="mt-5 border-t border-sand/10 pt-6 text-sm leading-relaxed text-sand/60">
                    {f.copy}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-32 lg:px-16">
        <SectionHeading
          eyebrow="What We Do"
          title={<>One studio. <span className="text-gold-dark">The whole creative lifecycle.</span></>}
          sub="Strategy → Content → Marketing → Production → Growth. Come to us with a full company or a single sentence — either works."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <Link
                to={p.to}
                data-testid={p.testId}
                className="group flex h-full flex-col border border-ink/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <p.icon size={26} className="text-gold-dark" />
                  <span className="font-mono text-[10px] tracking-[0.3em] text-ink/30">{p.num}</span>
                </div>
                <h3 className="mt-8 font-display text-xl font-bold tracking-tight text-ink">{p.title}</h3>
                <p className="mt-1.5 font-serif text-lg italic text-gold-dark">{p.serif}</p>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-smoke">{p.copy}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.18em] text-ink transition-colors duration-300 group-hover:text-gold-dark">
                  Explore
                  <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="mt-8 flex flex-col gap-5 border-2 border-gold/50 bg-gold-light/40 p-7 sm:flex-row sm:items-center sm:justify-between sm:p-9">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">
                Starting from scratch?
              </p>
              <p className="mt-2 font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">
                We can build the identity too.
              </p>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-smoke">
                Naming, logos, colors, typography, messaging and launch assets — a complete visual
                identity before we ever hit record.
              </p>
            </div>
            <GhostLink to="/branding" testId="home-branding-link" className="shrink-0">
              Explore Branding
            </GhostLink>
          </div>
        </Reveal>
      </section>

      {/* TRUSTED BY — LOGO MARQUEE */}
      <section className="overflow-hidden border-y border-ink/10 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-14 sm:px-8 lg:px-16">
          <Reveal>
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.35em] text-ink/40">
              Trusted by Wisconsin businesses, organizations & creators
            </p>
          </Reveal>
        </div>
        <div className="pb-14 pt-9">
          <div className="flex w-max animate-marquee [animation-duration:75s] hover:[animation-play-state:paused]">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex shrink-0 items-center" aria-hidden={dup === 1}>
                {TRUSTED_LOGOS.map((l) =>
                  l.dark ? (
                    <span key={l.name} className="mx-7 inline-flex items-center bg-night px-5 py-3">
                      <img src={l.src} alt={l.name} className="h-8 w-auto object-contain sm:h-10" />
                    </span>
                  ) : (
                    <img
                      key={l.name}
                      src={l.src}
                      alt={l.name}
                      data-testid={`logo-${l.name.toLowerCase().replace(/\s+/g, "-").replace(/\./g, "")}`}
                      className="mx-7 h-10 w-auto object-contain opacity-75 transition-opacity duration-300 hover:opacity-100 sm:h-14"
                    />
                  )
                )}
                {TRUSTED_BY.map((name) => (
                  <span
                    key={name}
                    className="mx-7 whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40"
                  >
                    {name}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPIN WHEEL */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-32 lg:px-16">
        <SectionHeading
          eyebrow="Featured Work"
          title={<>Take the work <span className="text-gold-dark">for a spin.</span></>}
          sub="Commercials, content engines, photography and studios — click a frame or use the arrows and play around."
        />
        <div className="mt-10">
          <SpinWheel projects={WHEEL} />
        </div>
        <Reveal className="mt-16 text-center">
          <p className="font-serif text-2xl italic text-ink sm:text-3xl">
            Like what you see? <span className="text-gold-dark">Let's build something.</span>
          </p>
          <div className="mt-7">
            <GoldLink to="/start-a-project" testId="wheel-cta-start-project">
              Start a Project
            </GoldLink>
          </div>
        </Reveal>
      </section>

      {/* MARKETING QUESTIONS */}
      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-32 lg:px-16">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Marketing & Content"
                title={<>We don't just make things look good. <span className="text-gold-dark">We help you figure out what to say.</span></>}
                sub="Then we create the content that says it — and put it in front of the people who matter."
              />
              <Reveal delay={0.15}>
                <div className="mt-8">
                  <GhostLink to="/marketing" testId="home-marketing-link">
                    Explore Marketing
                  </GhostLink>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <ul className="space-y-4">
                  {MARKETING_QUESTIONS.map((q) => (
                    <li key={q} className="flex gap-4 border-b border-ink/10 pb-4 font-serif text-xl italic text-ink sm:text-2xl">
                      <span className="mt-2.5 block h-2 w-2 shrink-0 bg-gold" />
                      {q}
                    </li>
                  ))}
                </ul>
                <p className="mt-8 font-display text-lg font-bold tracking-tight text-gold-dark sm:text-xl">
                  Then Studio Solutions actually executes the answers.
                </p>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-smoke">
                  A traditional agency develops the idea and outsources the production. A production
                  company waits for someone else's idea. We do both.
                </p>
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-ink/45">
                  Strategy <span className="text-gold">+</span> Creation <span className="text-gold">+</span> Execution
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* STUDIO BUILDS CALLOUT */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-32 lg:px-16">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="clip-frame overflow-hidden shadow-2xl">
              <MediaImg src="/assets/media/studio-b.jpg" alt="Interview studio built by Studio Solutions" className="h-[420px] w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionHeading
              eyebrow="Studio Builds"
              title={<>We don't just create content. <span className="text-gold-dark">We build the places where content gets created.</span></>}
              sub="Podcast studios, video studios, content rooms and livestream spaces — for businesses, churches, creators and organizations."
            />
            <Reveal delay={0.15}>
              <p className="mt-7 font-mono text-[11px] uppercase tracking-[0.25em] text-ink/45">
                Design → Equipment → Install → Configure → Train
              </p>
              <div className="mt-8">
                <GhostLink to="/systems" testId="home-studio-builds-link">
                  See Studio Builds
                </GhostLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* S3 FILMS */}
      <section className="border-y border-ink/10 bg-gold-light/40">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-8 sm:py-32 lg:grid-cols-2 lg:px-16">
          <div>
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold-dark">
                S3 Films · The Cinematic Division of Studio Solutions
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="headline mt-5 font-serif text-4xl italic leading-[1.1] text-ink sm:text-6xl">
                Where Story. Scene. Sound. began.
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-lg leading-relaxed text-smoke">
                S3 Films is the entire cinematic and video side of Studio Solutions — weddings, music
                videos, documentaries, promos, and story-driven content. Real voices, honest moments,
                and sound you'll want to hear again in thirty years.
              </p>
              <p className="mt-4 max-w-lg font-serif text-lg italic text-smoke">
                The philosophy that named this division now guides everything we make.
              </p>
              <div className="mt-8">
                <GhostLink to="/s3-films" testId="home-s3-films-link">
                  Explore S3 Films
                </GhostLink>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-4">
              <MediaImg src={IMAGES.wedding} alt="Wedding film still" className="h-64 w-full object-cover sm:h-80" />
              <MediaImg src={IMAGES.ceremony} alt="Ceremony detail" className="mt-10 h-64 w-full object-cover sm:h-80" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* DIFFERENTIATOR */}
      <section className="border-t border-ink/10 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-32 lg:px-16">
          <SectionHeading
            eyebrow="Why One Studio"
            title={<>The right team <span className="text-gold-dark">for the project.</span></>}
            sub="Most projects fail in the gaps between vendors. We removed the gaps."
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="h-full border border-ink/10 bg-sand p-8 sm:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-ink/40">The old way</p>
                <h3 className="mt-4 font-display text-2xl font-bold text-ink/60">A patchwork of vendors</h3>
                <ul className="mt-7 space-y-5">
                  {[
                    "A designer here, a videographer there, a web person who vanished",
                    "Five vendors, five contracts, five versions of the plan",
                    "Everyone owns their piece; nobody owns the outcome",
                    "When something breaks, the finger-pointing begins",
                  ].map((t) => (
                    <li key={t} className="flex gap-4 text-sm leading-relaxed text-smoke">
                      <span className="mt-1 block h-2 w-2 shrink-0 bg-ink/25" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="h-full border-2 border-gold bg-night p-8 text-sand sm:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">The Studio Solutions way</p>
                <h3 className="mt-4 font-display text-2xl font-bold">One direction. The right specialists.</h3>
                <ul className="mt-7 space-y-5">
                  {[
                    "Creative direction and communication centralized through Studio Solutions",
                    "Trusted filmmakers, photographers, editors, designers and specialists matched to what your project actually needs",
                    "Scaled to fit — a campaign doesn't need a film crew, and a commercial doesn't need a logo designer",
                    "One relationship, one standard, one point of contact",
                  ].map((t) => (
                    <li key={t} className="flex gap-4 text-sm leading-relaxed text-sand/80">
                      <span className="mt-1 block h-2 w-2 shrink-0 bg-gold" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-night text-sand">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-32 lg:px-16">
          <SectionHeading
            dark
            eyebrow="How It Works"
            title={<>Four steps. <span className="text-gold">No surprises.</span></>}
            sub="The same disciplined process runs a full campaign, a commercial shoot, and a studio build."
          />
          <div className="mt-14 grid gap-px bg-sand/10 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.08} className="bg-night">
                <div className="group h-full p-8 transition-colors duration-500 hover:bg-sand/5 sm:p-9">
                  <p className="font-mono text-sm tracking-[0.3em] text-gold">{s.num}</p>
                  <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{s.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-sand/60">{s.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WORK SHOWCASE */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-32 lg:px-16">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="relative overflow-hidden shadow-2xl">
              <video
                src={VIDEOS.showcase}
                poster="/assets/media/poster-showcase.jpg"
                autoPlay
                muted
                loop
                playsInline
                className="h-[380px] w-full object-cover sm:h-[480px]"
                data-testid="home-showcase-video"
              />
              <p className="absolute bottom-4 left-4 bg-night/50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-sand backdrop-blur-sm">
                Real client work · On repeat
              </p>
            </div>
          </Reveal>
          <div>
            <SectionHeading
              eyebrow="Work Showcase"
              title={<>Real work. <span className="text-gold-dark">On repeat.</span></>}
              sub="Commercials, weddings, livestreams and training series — a loop of what we actually make, week after week."
            />
            <Reveal delay={0.15}>
              <div className="mt-8">
                <GhostLink to="/work" testId="case-study-more-link">
                  Explore the Work
                </GhostLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ONGOING PARTNERSHIP */}
      <section className="bg-night text-sand">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <SectionHeading
              dark
              eyebrow="Ongoing Partnerships"
              title={<>Your creative team. <span className="text-gold">Without building one.</span></>}
              sub="One creative partner keeping everything consistent — month after month, without a single new hire."
            />
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-3">
                {["Strategy", "Video", "Photography", "Design", "Social Content", "Campaign Creative"].map((t) => (
                  <span key={t} className="border border-sand/20 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-sand/70">
                    {t}
                  </span>
                ))}
              </div>
              <div className="mt-9">
                <GoldLink to="/marketing" testId="home-partnership-cta">
                  Explore a Partnership
                </GoldLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-ink/10 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-32 lg:px-16">
          <SectionHeading
            eyebrow="Kind Words"
            title={<>Clients say it <span className="text-gold-dark">better.</span></>}
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <figure
                  data-testid={`testimonial-${i}`}
                  className="flex h-full flex-col border border-ink/10 bg-sand p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <span className="font-serif text-6xl leading-none text-gold">“</span>
                  <blockquote className="mt-2 flex-1 font-serif text-xl italic leading-relaxed text-ink">
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-7 border-t border-ink/10 pt-5">
                    <p className="font-display text-sm font-bold uppercase tracking-widest text-ink">{t.name}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">{t.org}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Start with an idea. We'll build the rest."
        sub="Two minutes in our inquiry form turns into a real plan — through HoneyBook, usually within one business day."
      />
    </div>
  );
}
